import { MessageBubble } from "@ui/components/MessageBubble";
import { formatTimeOfDay } from "@ui/lib/format-message-time";
import { tv } from "@ui/lib/tv";
import type { ComponentType, ReactNode } from "react";

interface MessageGroupItem {
  id: number;
  text: string;
  timestamp: string;
}

const messageGroup = tv({
  slots: {
    root: "flex items-end gap-2",
    column: "flex min-w-0 max-w-[min(32rem,78%)] flex-col gap-1",
    meta: "flex items-baseline gap-2 px-1 text-xs",
    message: "flex flex-col gap-1",
  },
  variants: {
    isOwn: {
      // Own messages sit on the trailing edge with no avatar: on your own side of a thread the
      // portrait carries no information the alignment doesn't already give.
      true: { root: "flex-row-reverse", column: "items-end", meta: "flex-row-reverse", message: "items-end" },
      false: {},
    },
  },
});

export interface MessageGroupProps {
  isOwn?: boolean;
  messages: MessageGroupItem[];
  /** Rendered under the run's last bubble — read receipts, reactions. */
  footer?: ReactNode;
  /** Rendered under each bubble. Receives the message it belongs to. */
  renderMessageFooter?: (message: MessageGroupItem) => ReactNode;
  /** Rendered above each bubble — attached images, which read as part of the same utterance. */
  renderMessageMedia?: (message: MessageGroupItem) => ReactNode;
  /** Rendered beside each bubble on hover/focus. Receives the message it belongs to. */
  renderMessageActions?: (message: MessageGroupItem) => ReactNode;
  /** Wraps each bubble's row. A component rather than a props function so it can hold hooks — a
   *  press gesture needs them. */
  MessageRow?: ComponentType<{ children: ReactNode; message: MessageGroupItem }>;
  sender: { avatar: ReactNode; name: string };
}

export function MessageGroup({
  footer,
  isOwn = false,
  MessageRow = PlainRow,
  messages,
  renderMessageActions,
  renderMessageFooter,
  renderMessageMedia,
  sender,
}: MessageGroupProps) {
  const styles = messageGroup({ isOwn });
  const firstMessage = messages[0];
  const variant = isOwn ? "own" : "default";

  return (
    <div className={styles.root()}>
      {isOwn ? null : <div className="size-8 shrink-0">{sender.avatar}</div>}
      <div className={styles.column()}>
        <div className={styles.meta()}>
          {isOwn ? null : <span className="font-semibold text-neutral-700">{sender.name}</span>}
          {firstMessage && <span className="text-neutral-400">{formatTimeOfDay(firstMessage.timestamp)}</span>}
        </div>
        {messages.map((message, index) => (
          <div key={message.id} className={styles.message()}>
            {renderMessageMedia?.(message)}
            <MessageRow message={message}>
              <div className="group/message flex items-center gap-1.5">
                {isOwn && renderMessageActions?.(message)}
                {message.text.length > 0 && (
                  <MessageBubble tail={index === messages.length - 1} variant={variant}>
                    {message.text}
                  </MessageBubble>
                )}
                {isOwn ? null : renderMessageActions?.(message)}
              </div>
            </MessageRow>
            {renderMessageFooter?.(message)}
          </div>
        ))}
        {footer}
      </div>
    </div>
  );
}

function PlainRow({ children }: { children: ReactNode }) {
  return children;
}
